export type PageSectionLocator =
  | {
      kind: 'heading';
      text: string;
      closest?: string;
    }
  | {
      kind: 'selector';
      selector: string;
    };

interface PageSectionDefinition {
  label: string;
  locator: PageSectionLocator;
}

interface PageTargetDefinition {
  label: string;
  url: string;
  sections: Record<string, PageSectionDefinition>;
}

/**
 * Canonical allow-list for every page and DOM location Copilot may control.
 * Selectors and heading text are verified against the official IPMA pages and
 * never come from the model or an action payload.
 */
export const pageTargetRegistry = {
  home: {
    label: 'Open IPMA home',
    url: 'https://ipma.world/',
    sections: {}
  },
  certifications: {
    label: 'Open certifications',
    url: 'https://ipma.world/ipma-certification/',
    sections: {}
  },
  certificationLevelA: {
    label: 'View Level A certification',
    url: 'https://ipma.world/ipma-certification/4lc/certified-projects-director-level-a/',
    sections: {}
  },
  certificationLevelB: {
    label: 'View Level B certification',
    url: 'https://ipma.world/ipma-certification/4lc/certified-senior-project-manager-level-b/',
    sections: {
      overview: {
        label: 'Show Level B overview',
        locator: {
          kind: 'heading',
          text: 'Become Recognised as a Professional – IPMA Level B Certification',
          closest: '.text.wp-block-text'
        }
      },
      requirements: {
        label: 'Show Level B requirements',
        locator: {
          kind: 'heading',
          text: 'Entry Criteria for Senior Project Manager',
          closest: '.text.wp-block-text'
        }
      },
      programmeRequirements: {
        label: 'Show Level B programme requirements',
        locator: {
          kind: 'heading',
          text: 'Entry Criteria for Senior Programme Manager',
          closest: '.text.wp-block-text'
        }
      },
      portfolioRequirements: {
        label: 'Show Level B portfolio requirements',
        locator: {
          kind: 'heading',
          text: 'Entry Criteria for Senior Portfolio Manager',
          closest: '.text.wp-block-text'
        }
      },
      application: {
        label: 'Go to the Level B application contact',
        locator: {
          kind: 'heading',
          text: 'Contact IPMA to learn more about certification process',
          closest: '.contact.wp-block-contact'
        }
      }
    }
  },
  certificationLevelC: {
    label: 'View Level C certification',
    url: 'https://ipma.world/ipma-certification/4lc/certified-project-manager-level-c/',
    sections: {
      overview: {
        label: 'Show Level C overview',
        locator: {
          kind: 'heading',
          text: 'Become Recognised as a Professional – IPMA Level C Certification',
          closest: '.text.wp-block-text'
        }
      },
      requirements: {
        label: 'Show Level C requirements',
        locator: {
          kind: 'heading',
          text: 'Entry Criteria for Project Manager',
          closest: '.text.wp-block-text'
        }
      },
      application: {
        label: 'Go to the Level C application contact',
        locator: {
          kind: 'heading',
          text: 'Contact IPMA to learn more about certification process',
          closest: '.contact.wp-block-contact'
        }
      }
    }
  },
  certificationLevelD: {
    label: 'View Level D certification',
    url: 'https://ipma.world/ipma-certification/4lc/certified-project-management-associate-level-d/',
    sections: {}
  },
  certificationProcess: {
    label: 'View the certification process',
    url: 'https://ipma.world/ipma-certification/4lc/certification-process/',
    sections: {}
  },
  events: {
    label: 'Open IPMA events',
    url: 'https://ipma.world/events/',
    sections: {
      registration: {
        label: 'Show event registration',
        locator: {
          kind: 'selector',
          selector: 'main#site-main a[href="https://eventus.ipma.world/"]'
        }
      },
      upcoming: {
        label: 'Show upcoming events',
        locator: {
          kind: 'selector',
          selector: 'main#site-main .calendar.wp-block-calendar'
        }
      }
    }
  },
  membership: {
    label: 'Explore IPMA membership',
    url: 'https://ipma.world/ipma-membership/',
    sections: {
      join: {
        label: 'Find a local Member Association',
        locator: {
          kind: 'heading',
          text: 'IPMA Member Associations (IPMA MAs)',
          closest: '.maps.wp-block-map'
        }
      }
    }
  },
  membershipBenefits: {
    label: 'Explore membership benefits',
    url: 'https://ipma.world/why-become-an-individual-member-of-ipmas-member-association-2/',
    sections: {
      benefits: {
        label: 'Show membership benefits',
        locator: {
          kind: 'selector',
          selector: 'main#site-main article .content ul'
        }
      }
    }
  },
  icb4: {
    label: 'Learn about ICB4',
    url: 'https://ipma.world/ipma-standards-development-programme/icb4/',
    sections: {}
  },
  standards: {
    label: 'Explore IPMA standards',
    url: 'https://ipma.world/ipma-standards-development-programme/',
    sections: {}
  },
  about: {
    label: 'Learn about IPMA',
    url: 'https://ipma.world/about-us/',
    sections: {}
  },
  contact: {
    label: 'Contact IPMA',
    url: 'https://ipma.world/about-us/contact-us/',
    sections: {
      overview: {
        label: 'Show IPMA contact information',
        locator: {
          kind: 'heading',
          text: 'Our Secretariat',
          closest: '.text.wp-block-text'
        }
      },
      contactForm: {
        label: 'Show the IPMA contact form',
        locator: {
          kind: 'heading',
          text: 'We can answer your IPMA questions! Please fill out this form and we will get in touch with you shortly.',
          closest: '.contact.wp-block-contact'
        }
      }
    }
  },
  news: {
    label: 'Read IPMA news',
    url: 'https://ipma.world/category/news/',
    sections: {}
  }
} as const satisfies Record<string, PageTargetDefinition>;

export type NavigationTarget = keyof typeof pageTargetRegistry;
export type PageSection = {
  [Target in NavigationTarget]: keyof (typeof pageTargetRegistry)[Target]['sections'];
}[NavigationTarget] &
  string;

export const isNavigationTarget = (
  target: unknown
): target is NavigationTarget =>
  typeof target === 'string' &&
  Object.prototype.hasOwnProperty.call(pageTargetRegistry, target);

export const isPageSection = (
  target: NavigationTarget,
  section: unknown
): section is PageSection => {
  if (typeof section !== 'string') return false;

  const sections = pageTargetRegistry[target].sections as Record<
    string,
    PageSectionDefinition
  >;
  return Object.prototype.hasOwnProperty.call(sections, section);
};

export const getPageSectionDefinition = (
  target: NavigationTarget,
  section: PageSection
): PageSectionDefinition | null => {
  if (!isPageSection(target, section)) return null;

  const sections = pageTargetRegistry[target].sections as Record<
    string,
    PageSectionDefinition
  >;
  return sections[section];
};
